import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Plus, Search, Building2, Wallet, Copy, ExternalLink,
  Trash2, Edit2, Save, X, Filter, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Address categories
const CATEGORIES = [
  { value: 'bank', label: 'Banks & Custodians', color: 'blue' },
  { value: 'exchange', label: 'Exchanges', color: 'purple' },
  { value: 'client', label: 'Clients', color: 'green' },
  { value: 'partner', label: 'Partners', color: 'yellow' },
  { value: 'internal', label: 'Internal', color: 'gray' }
];

const AddressBook = () => {
  const { token } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [newAddress, setNewAddress] = useState({
    name: '',
    wallet_address: '',
    category: 'client',
    description: '',
    contact_email: ''
  });

  useEffect(() => {
    fetchAddresses();
  }, [token]);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/address-book`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAddresses(data.addresses || []);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.name || !newAddress.wallet_address) {
      toast.error('Name and wallet address are required');
      return;
    }
    
    // Validate wallet address format
    if (!newAddress.wallet_address.match(/^0x[a-fA-F0-9]{40}$/)) {
      toast.error('Invalid Ethereum address format');
      return;
    }

    try {
      const response = await fetch(`${API}/address-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newAddress)
      });
      
      if (response.ok) {
        const data = await response.json();
        setAddresses([data.address, ...addresses]);
        setNewAddress({ name: '', wallet_address: '', category: 'client', description: '', contact_email: '' });
        setShowAddForm(false);
        toast.success('Address added successfully');
      } else {
        toast.error('Failed to add address');
      }
    } catch (error) {
      toast.error('Error adding address');
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      const response = await fetch(`${API}/address-book/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setAddresses(addresses.filter(a => a.id !== id));
        toast.success('Address deleted');
      }
    } catch (error) {
      toast.error('Error deleting address');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    const colors = {
      blue: 'bg-blue-900/30 border-blue-500/30 text-blue-400',
      purple: 'bg-purple-900/30 border-purple-500/30 text-purple-400',
      green: 'bg-green-900/30 border-green-500/30 text-green-400',
      yellow: 'bg-yellow-900/30 border-yellow-500/30 text-yellow-400',
      gray: 'bg-gray-900/30 border-gray-500/30 text-gray-400'
    };
    return colors[cat?.color] || colors.gray;
  };

  const filteredAddresses = addresses.filter(addr => {
    const matchesSearch = addr.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         addr.wallet_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         addr.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || addr.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const exportAddresses = () => {
    const csv = [
      ['Name', 'Wallet Address', 'Category', 'Description', 'Contact Email'],
      ...filteredAddresses.map(a => [a.name, a.wallet_address, a.category, a.description, a.contact_email])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'address_book.csv';
    link.click();
    toast.success('Address book exported');
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="address-book">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-2">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-6 h-6 text-[#b8860b]" />
                Institutional Address Book
              </h1>
              <p className="text-gray-400 text-sm">Manage wallet addresses for banks, custodians, and partners</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportAddresses}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button className="bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => setShowAddForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Address
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search by name, address, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0d1d35] border-[#1a2d4a] text-white"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={categoryFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter('all')}
              className={categoryFilter === 'all' ? 'bg-[#b8860b]' : ''}
            >
              All
            </Button>
            {CATEGORIES.map(cat => (
              <Button
                key={cat.value}
                variant={categoryFilter === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter(cat.value)}
                className={categoryFilter === cat.value ? 'bg-[#b8860b]' : ''}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white">Add New Address</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Name *</label>
                  <Input
                    value={newAddress.name}
                    onChange={(e) => setNewAddress({...newAddress, name: e.target.value})}
                    placeholder="e.g., Coinbase Custody"
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Wallet Address *</label>
                  <Input
                    value={newAddress.wallet_address}
                    onChange={(e) => setNewAddress({...newAddress, wallet_address: e.target.value})}
                    placeholder="0x..."
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Category</label>
                  <select
                    value={newAddress.category}
                    onChange={(e) => setNewAddress({...newAddress, category: e.target.value})}
                    className="w-full p-2 rounded bg-[#1a2d4a] border border-[#2a3d5a] text-white"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Contact Email</label>
                  <Input
                    value={newAddress.contact_email}
                    onChange={(e) => setNewAddress({...newAddress, contact_email: e.target.value})}
                    placeholder="contact@example.com"
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-400 mb-1 block">Description</label>
                  <Input
                    value={newAddress.description}
                    onChange={(e) => setNewAddress({...newAddress, description: e.target.value})}
                    placeholder="Notes about this address..."
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleAddAddress} className="bg-[#b8860b] hover:bg-[#9a7209]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Address
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Address List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : filteredAddresses.length === 0 ? (
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardContent className="py-8 text-center">
                <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No addresses found</p>
                <Button 
                  className="mt-4 bg-[#b8860b]" 
                  onClick={() => setShowAddForm(true)}
                >
                  Add Your First Address
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredAddresses.map((addr) => (
              <Card key={addr.id} className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg border ${getCategoryColor(addr.category)}`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{addr.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{addr.wallet_address}</p>
                        {addr.description && (
                          <p className="text-xs text-gray-400 mt-1">{addr.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded border ${getCategoryColor(addr.category)}`}>
                        {CATEGORIES.find(c => c.value === addr.category)?.label}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(addr.wallet_address, 'Address')}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <a 
                        href={`https://etherscan.io/address/${addr.wallet_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteAddress(addr.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {CATEGORIES.map(cat => {
            const count = addresses.filter(a => a.category === cat.value).length;
            return (
              <Card key={cat.value} className={`border ${getCategoryColor(cat.value)}`}>
                <CardContent className="py-3 text-center">
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-xs text-gray-400">{cat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AddressBook;
